// Main menu flow: title → world select → create (UI-6). Consolidated into one
// file (title/worldselect/create) for cohesion; pause/options are separate.
import { h, clear } from '../h';
import { showHelp } from './help';
import { FLAT_PRESETS, GAMERULE_TOGGLES } from '../../world_presets';
import { exportWorldFile, downloadWorldFile } from '../../storage/export';
import { importWorldFile } from '../../storage/import';
import { loadOverlay } from '../../storage/overlay';
import type { SaveManager, WorldMeta } from '../../storage/saves';
import { EARTH } from '../../../sim/config';
import { earthBadge } from '../../../sim/earth/badge';
import { earthMetaForCreate, earthRangeSpec } from '../../earth';
import { HEREDITY_RULE } from '../../../sim/heredity/rule';
import type { LessonDef } from '../../../sim/registry/schemas';

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'world';
}

export function showMenu(
  save: SaveManager, onPlay: (meta: WorldMeta, isNew: boolean) => void, lessons: readonly LessonDef[] = [],
): void {
  const root = h('div', { class: 'bb-screen' });
  document.body.append(root);
  const play = (meta: WorldMeta, isNew: boolean): void => {
    root.remove();
    onPlay(meta, isNew);
  };

  const title = (): void => {
    clear(root);
    root.append(
      h('div', { class: 'bb-title' }, 'TerraForge'),
      h(
        'div',
        { class: 'bb-panel' },
        h('button', { class: 'bb-btn bb-primary', onclick: worlds }, 'Play'),
        h('button', { class: 'bb-btn', onclick: () => showHelp(() => {}) }, 'How to Play'),
        h('div', { class: 'bb-muted' }, 'Build, mine, explore a block world.'),
      ),
    );
  };

  const worlds = async (): Promise<void> => {
    clear(root);
    const list = h('div', { class: 'bb-worldlist' });
    const saved = await save.listWorlds();
    if (!saved.length) list.append(h('div', { class: 'bb-muted' }, 'No worlds yet — create one.'));
    for (const w of saved) {
      list.append(
        h(
          'div',
          { class: 'bb-worlditem' },
          h(
            'div',
            { class: 'bb-wi-main', onclick: () => play(w, false) },
            h('div', { class: 'bb-wi-name' }, w.name),
            h('div', { class: 'bb-wi-sub' }, w.worldType === 'earth' && w.earth
              ? `earth · ${earthBadge(w.earth.demSource ?? 'srtm-30m', w.earth.scaleM).text}`
              : `${w.worldType} · seed ${w.seed}`),
          ),
          h(
            'button',
            {
              class: 'bb-btn',
              style: { flex: '0 0 auto' },
              'data-export': w.id,
              title: 'Download this world as a .tfworld file (import into a server)',
              onclick: async () => {
                const text = await exportWorldFile(save, w.id);
                downloadWorldFile(text, w.id);
              },
            },
            'Export',
          ),
          h(
            'button',
            {
              class: 'bb-btn bb-danger',
              style: { flex: '0 0 auto' },
              'data-del': w.id,
              onclick: async () => {
                await save.deleteWorld(w.id);
                void worlds();
              },
            },
            'Delete',
          ),
        ),
      );
    }
    root.append(
      h('div', { class: 'bb-title' }, 'Worlds'),
      h(
        'div',
        { class: 'bb-panel' },
        list,
        h(
          'div',
          { class: 'bb-row' },
          h('button', { class: 'bb-btn', onclick: title }, 'Back'),
          h('button', { class: 'bb-btn bb-primary', onclick: create }, 'New World'),
        ),
      ),
    );
  };

  // §29.3 (EDU-2, M51.6): the create screen's Lesson tab, beside today's Custom
  // World form — lists every lesson in the loaded collections, grouped by subject.
  const createFromLesson = async (lesson: LessonDef): Promise<void> => {
    const id = `${slug(lesson.title)}-${Date.now().toString(36)}`;
    // Solo has exactly one active overlay (D-TF-256's own scope note applies here
    // too: a bare `.collection`/`.version` per lesson would need per-lesson
    // provenance the overlay-merge pipeline doesn't track — the loaded overlay's
    // own id stands in for "which collection this came from" in solo).
    const overlay = loadOverlay();
    const lessonMeta: NonNullable<WorldMeta['lesson']> = {
      name: lesson.name, collection: overlay.collection, version: overlay.version, startedTick: 0,
    };
    let meta: WorldMeta;
    if (lesson.template) {
      const res = await importWorldFile(save, lesson.template, { id, name: lesson.title, gamerules: lesson.gamerules });
      meta = { ...res.meta, lesson: lessonMeta };
    } else {
      // E-57's sibling case: no template AT ALL (not "missing from the archive" —
      // that is the server/`/lesson create` case) simply creates an empty world.
      meta = {
        id, name: lesson.title, seed: String(Date.now()), worldType: 'flat', gameMode: 'survival',
        difficulty: 'peaceful', allowCommands: true, gamerules: { ...lesson.gamerules }, lesson: lessonMeta,
        createdAt: Date.now(), playedAt: Date.now(), version: 1,
      };
      await save.putWorld(meta);
    }
    play(meta, true);
  };

  const renderLessonTab = (content: HTMLElement): void => {
    if (lessons.length === 0) {
      content.append(
        h('div', { class: 'bb-panel', 'data-testid': 'lesson-tab-empty' }, 'No lesson packs are loaded.'),
        h('div', { class: 'bb-row' }, h('button', { class: 'bb-btn', onclick: worlds }, 'Back')),
      );
      return;
    }
    const bySubject = new Map<string, LessonDef[]>();
    const sorted = [...lessons].sort((a, b) => (a.subject === b.subject ? a.title.localeCompare(b.title) : a.subject.localeCompare(b.subject)));
    for (const l of sorted) (bySubject.get(l.subject) ?? bySubject.set(l.subject, []).get(l.subject)!).push(l);
    const overlay = loadOverlay();
    const panel = h('div', { class: 'bb-panel', 'data-testid': 'lesson-tab-list' });
    for (const [subject, group] of bySubject) {
      panel.append(h('div', { class: 'bb-label' }, subject));
      for (const lesson of group) {
        panel.append(
          h(
            'div',
            { class: 'bb-worlditem', 'data-testid': `lesson-card-${lesson.name}` },
            h(
              'div',
              { class: 'bb-wi-main' },
              h('div', { class: 'bb-wi-name' }, lesson.title),
              h('div', { class: 'bb-wi-sub' }, lesson.description),
              h('div', { class: 'bb-muted' }, `${overlay.collection}@${overlay.version}`),
            ),
            h(
              'button',
              { class: 'bb-btn bb-primary', style: { flex: '0 0 auto' }, 'data-testid': `lesson-create-${lesson.name}`, onclick: () => void createFromLesson(lesson) },
              'Create',
            ),
          ),
        );
      }
    }
    content.append(panel, h('div', { class: 'bb-row' }, h('button', { class: 'bb-btn', onclick: worlds }, 'Back')));
  };

  const renderCustomTab = (content: HTMLElement): void => {
    const name = h('input', { class: 'bb-input', value: 'New World', maxLength: 32 });
    const seed = h('input', { class: 'bb-input', placeholder: 'seed (blank = random)' });
    const type = h('select', { class: 'bb-select' }, h('option', { value: 'normal' }, 'Normal'), h('option', { value: 'flat' }, 'Flat'), h('option', { value: 'atlas' }, 'Atlas (real city)'), h('option', { value: 'earth' }, 'Earth (real place)')) as HTMLSelectElement;
    const mode = h('select', { class: 'bb-select' }, h('option', { value: 'creative' }, 'Creative'), h('option', { value: 'survival' }, 'Survival')) as HTMLSelectElement;
    const cheats = h('input', { type: 'checkbox', checked: true }) as HTMLInputElement;
    // §28.2 (HER-1): heredity is a per-world choice, off by default. The label is binding.
    const heredity = h('input', { type: 'checkbox', checked: false, 'data-testid': 'create-heredity' }) as HTMLInputElement;

    // Atlas panel (UI-6, §ATL): revealed only for the Atlas world type.
    const location = h('input', { class: 'bb-input', placeholder: '269 Maxim Rd, Hartford CT (blank = default)' }) as HTMLInputElement;
    const scale = h('input', { class: 'bb-input', type: 'number', value: '1', min: '1', max: '10' }) as HTMLInputElement;
    const atlasPanel = h('div', { style: { display: 'none' } },
      h('div', { class: 'bb-label' }, 'Location'), location,
      h('div', { class: 'bb-label' }, 'Scale (metres per block)'), scale,
      h('div', { class: 'bb-muted' }, 'Blank location renders 269 Maxim Rd (KHFD) offline.'),
    );

    // Earth panel (UI-6, §ERT): revealed only for the Earth world type — a real place
    // with real elevation, at a per-world editable scale (default 3 ft/block).
    const earthLocation = h('input', { class: 'bb-input', placeholder: '269 Maxim Rd, Hartford CT (blank = default)' }) as HTMLInputElement;
    const earthScale = h('input', { class: 'bb-input', type: 'number', value: String(EARTH.DEFAULT.scaleM), min: '0.1', max: '20', step: '0.0001' }) as HTMLInputElement;
    const earthPanel = h('div', { style: { display: 'none' } },
      h('div', { class: 'bb-label' }, 'Location'), earthLocation,
      h('div', { class: 'bb-label' }, 'Scale (metres per block)'), earthScale,
      h('div', { class: 'bb-muted' }, 'Blank location stands you at 269 Maxim Rd (KHFD) offline — real terrain to ±1 block.'),
    );

    // Flat preset (M24): revealed only for the Flat world type.
    const flatPreset = h('select', { class: 'bb-select' },
      ...FLAT_PRESETS.map((p) => h('option', { value: p.id }, p.label))) as HTMLSelectElement;
    const flatPanel = h('div', { style: { display: 'none' } },
      h('div', { class: 'bb-label' }, 'Flat Preset'), flatPreset);

    // Game rules (M24): a checkbox per engine-honoured rule, applied at creation.
    const ruleChecks = new Map<string, HTMLInputElement>();
    const rulesPanel = h('div', {},
      h('div', { class: 'bb-label' }, 'Game Rules'),
      ...GAMERULE_TOGGLES.map((g) => {
        const c = h('input', { type: 'checkbox', checked: g.default }) as HTMLInputElement;
        ruleChecks.set(g.id, c);
        return h('label', { class: 'bb-row bb-check' }, c, h('span', null, g.label));
      }),
    );

    const onTypeChange = (): void => {
      const atlas = type.value === 'atlas', earth = type.value === 'earth';
      atlasPanel.style.display = atlas ? 'block' : 'none';
      earthPanel.style.display = earth ? 'block' : 'none';
      flatPanel.style.display = type.value === 'flat' ? 'block' : 'none';
      if (atlas || earth) { mode.value = 'creative'; cheats.checked = true; } // ATL-6/ERT-6 defaults
    };
    type.onchange = onTypeChange;
    // Cheats default follows the mode (on for creative, off for survival) but stays
    // overridable — so a survival world *can* keep commands, and vice-versa.
    mode.onchange = () => { cheats.checked = mode.value === 'creative'; };

    const submit = async (): Promise<void> => {
      const atlas = type.value === 'atlas', earth = type.value === 'earth';
      // Earth: a blank location is the offline KHFD default; a typed one geocodes once
      // (ATL-7 reuse). Scale is metres/block (default 3 ft), editable per world (ERT-1).
      let earthMeta: WorldMeta['earth'];
      if (earth) {
        const loc = earthLocation.value.trim();
        const scaleM = Math.max(0.1, Math.min(20, Number(earthScale.value) || EARTH.DEFAULT.scaleM));
        try {
          earthMeta = await earthMetaForCreate(loc, scaleM);
        } catch (err) {
          alert(`Could not find that location: ${(err as Error).message}`);
          return;
        }
      }
      const meta: WorldMeta = {
        id: `${slug(name.value)}-${Date.now().toString(36)}`,
        name: name.value || 'World',
        seed: seed.value || String(Date.now()),
        worldType: type.value as WorldMeta['worldType'],
        gameMode: mode.value as WorldMeta['gameMode'],
        difficulty: 'normal',
        allowCommands: cheats.checked,
        atlas: atlas ? { location: location.value.trim() || undefined, scale: Math.max(1, Math.min(10, Number(scale.value) || 1)) } : undefined,
        earth: earthMeta,
        ranges: earth ? { overworld: earthRangeSpec(earthMeta!.scaleM) } : undefined,
        flatPreset: type.value === 'flat' ? flatPreset.value : undefined,
        gamerules: {
          ...Object.fromEntries(GAMERULE_TOGGLES.map((g) => [g.id, String(ruleChecks.get(g.id)!.checked)])),
          // Only when ticked: an unticked world's meta stays byte-identical to pre-M48.
          ...(heredity.checked ? { [HEREDITY_RULE]: 'true' } : {}),
        },
        createdAt: Date.now(),
        playedAt: Date.now(),
        version: 1,
      };
      await save.putWorld(meta);
      play(meta, true);
    };
    content.append(
      h(
        'div',
        { class: 'bb-panel' },
        h('div', { class: 'bb-label' }, 'Name'), name,
        h('div', { class: 'bb-label' }, 'Seed'), seed,
        h('div', { class: 'bb-label' }, 'Type'), type,
        atlasPanel,
        earthPanel,
        flatPanel,
        h('div', { class: 'bb-label' }, 'Mode'), mode,
        h('label', { class: 'bb-row bb-check' }, cheats, h('span', null, 'Allow Cheats (commands)')),
        h('label', { class: 'bb-row bb-check' }, heredity,
          h('span', null, 'Heredity — every animal and seed is an individual; breeding changes the flock over generations. Animals age.')),
        rulesPanel,
        h(
          'div',
          { class: 'bb-row' },
          h('button', { class: 'bb-btn', onclick: worlds }, 'Back'),
          h('button', { class: 'bb-btn bb-primary', onclick: submit }, 'Create'),
        ),
      ),
    );
  };

  // The outer create screen: a title + the Custom World / Lesson tab strip, over
  // whichever tab's content (§29.3's Lesson tab, EDU-2).
  const create = (tab: 'custom' | 'lesson' = 'custom'): void => {
    clear(root);
    const content = h('div');
    if (tab === 'lesson') renderLessonTab(content);
    else renderCustomTab(content);
    root.append(
      h('div', { class: 'bb-title' }, 'Create World'),
      h(
        'div',
        { class: 'bb-row' },
        h('button', { class: `bb-btn${tab === 'custom' ? ' bb-primary' : ''}`, 'data-testid': 'create-tab-custom', onclick: () => create('custom') }, 'Custom World'),
        h('button', { class: `bb-btn${tab === 'lesson' ? ' bb-primary' : ''}`, 'data-testid': 'create-tab-lesson', onclick: () => create('lesson') }, 'Lesson'),
      ),
      content,
    );
  };

  title();
}
